import React, { useRef } from 'react'
import { DateTime } from 'luxon'
import { Button } from 'primereact/button'
import { Menu } from 'primereact/menu'
import { Tag } from 'primereact/tag'
import CopyButton from '../../elements/CopyButton'
import { findFlowType, visualBuilderFlowType } from './catalog'
import { effectiveFlowTypeId, flowTypeUsage, overLimitFlowUuids } from './flowLimits'
import { flowOverLimitWarning, NO_NOTES } from './layoutBuilderCopy'
import { flowChangeNote } from './implementationChanges'
import { flowPath, newAiFlowPath } from './implementedRoutes'
import NewTabLink from './NewTabLink'

// F4: gently flag a gap (application/file type/protocol left as "Not specified") without
// touching the text or the underlying "unknown" logic - just a muted highlight class.
function EndpointValue ({ value }) {
  const missing = value === 'Not specified'
  return <dd className={missing ? 'layoutBuilder-flow-value-missing' : undefined}>{value}</dd>
}

function Endpoint ({ label, side }) {
  const application = side?.unknown ? 'Unknown' : side?.application_label || 'Not specified'
  const fileType = side?.file_type_label || 'Not specified'
  const protocol = side?.protocol || 'Not specified'
  return (
    <section className='layoutBuilder-flow-endpoint' aria-label={label}>
      <h4>{label}</h4>
      <dl>
        <dt>Application</dt><EndpointValue value={application} />
        <dt>File type</dt><EndpointValue value={fileType} />
        <dt>Protocol</dt><EndpointValue value={protocol} />
      </dl>
    </section>
  )
}

const hasFlow = (implementation, plannedFlowUuid) => implementation.flows.some(item => item.planned_flow_uuid === plannedFlowUuid)

const copiesOf = (implementation, plannedFlowUuid) => implementation.flows.filter(item => item.planned_flow_uuid === plannedFlowUuid)

// The trigger of each card dropdown: a stock outlined Button whose badge counts the items it holds. The
// theme's default button badge is white, which vanishes on an outlined button, so it takes the secondary severity.
const dropdownTriggerProps = count => ({ badge: String(count), badgeClassName: 'p-badge-secondary', icon: 'pi pi-chevron-down', iconPos: 'right', outlined: true, 'aria-haspopup': true })

// Popup menu items carry their full "<integration> › <flow>" name in `data`, since the visible label is
// only the flow name under an integration group header.
const accessibleName = ({ context }) => ({ 'aria-label': context.item.data?.accessibleName })
const openFlowMenuPt = {
  menuitem: accessibleName,
  action: options => ({ ...accessibleName(options), rel: 'noopener noreferrer' })
}

const copyKey = (implementation, flowName) => JSON.stringify([implementation.partner_name, flowName])

// The copy names (integration name + flow name) shared by copies in more than one implementation: the only
// case where nothing visible tells them apart, so only those get their integration's implemented time.
// Same-named copies within one implementation share that time, so it would not help them.
const ambiguousCopyKeys = (implementations, plannedFlowUuid, name) => {
  const partnersByKey = implementations.reduce((acc, implementation) => {
    copiesOf(implementation, plannedFlowUuid).forEach(item => {
      const key = copyKey(implementation, item.flow_name || name)
      acc.set(key, new Set([...(acc.get(key) || []), implementation.partner_uuid]))
    })
    return acc
  }, new Map())
  return new Set([...partnersByKey].filter(([, partners]) => partners.size > 1).map(([key]) => key))
}

const implementedWhen = implementation => DateTime.fromISO(implementation.implemented_at).toLocaleString(DateTime.DATETIME_MED)

// One group per implementation that has copies of this flow, headed by the integration name, with each copy,
// in creation order, as a stock menu item named by its flow name (the planned name when it has none) that
// opens the flow in a new tab.
const openFlowGroups = ({ name, plannedFlowUuid, implementations, companyUUID }) => {
  const ambiguous = ambiguousCopyKeys(implementations, plannedFlowUuid, name)
  return implementations
    .map(implementation => ({
      label: implementation.partner_name,
      items: copiesOf(implementation, plannedFlowUuid).map(item => {
        const flowName = item.flow_name || name
        const label = ambiguous.has(copyKey(implementation, flowName)) && implementation.implemented_at ? `${flowName} (${implementedWhen(implementation)})` : flowName
        return { label, icon: 'pi pi-external-link', url: flowPath(companyUUID, implementation.partner_uuid, item.flow_uuid), target: '_blank', data: { accessibleName: `${implementation.partner_name} › ${label}` } }
      })
    }))
    .filter(group => group.items.length > 0)
}

function OpenFlowMenu ({ name, groups }) {
  const menuRef = useRef(null)
  const count = groups.reduce((total, group) => total + group.items.length, 0)
  return (
    <>
      <Button
        label='Open flow'
        aria-label={`Open ${name} (${count} ${count === 1 ? 'copy' : 'copies'})`}
        {...dropdownTriggerProps(count)}
        onClick={event => menuRef.current.toggle(event)}
      />
      <Menu model={groups} popup ref={menuRef} className='layoutBuilder-action-menu' pt={openFlowMenuPt} />
    </>
  )
}

// One menu listing every implementation, in the same order as the Open groups. Adding a flow an
// implementation already has is allowed (it creates another draft copy), so those are marked, not hidden.
function ImplementInIntegration ({ name, flow, implementations, onAddFlow, loading, disabled }) {
  const menuRef = useRef(null)
  const items = implementations.map(implementation => ({
    label: hasFlow(implementation, flow.flow_uuid) ? `${implementation.partner_name} (already has this flow)` : implementation.partner_name,
    command: () => onAddFlow(implementation, flow)
  }))
  return (
    <>
      <Button
        label='Implement in integration'
        aria-label={`Implement ${name} in integration`}
        {...dropdownTriggerProps(implementations.length)}
        loading={loading}
        disabled={disabled}
        onClick={event => menuRef.current.toggle(event)}
      />
      <Menu model={items} popup ref={menuRef} className='layoutBuilder-action-menu' />
    </>
  )
}

// Links to the created flows and to Visual Builder open in a new tab, so they are real anchors built from
// `companyUUID`. companyUUID and onAddFlow(implementation, flow) are passed only where implemented
// flows are actionable (the writable workspace editor); `implementations` is the integration's list from
// the layout query. Any flow can be added to any implementation through the Implement in integration menu;
// while a review is being prepared every such button is disabled (`addFlowDisabled`) and the one for
// `preparingFlowUuid` shows its loading state.
// For a Visual Builder integration the editor passes instead `aiPrompt(flow)`, the text Copy AI prompt
// copies, and with it a Build in Visual Builder link per implementation, which starts a new AI flow there. onRemove(flow), passed
// wherever the layout is writable, offers Remove flow from layout on each card.
export default function FlowCards ({ flows, integrationType, readOnly, onOpen, implementations = [], companyUUID = null, onAddFlow = null, aiPrompt = null, onRemove = null, preparingFlowUuid = null, addFlowDisabled = false }) {
  const overLimit = overLimitFlowUuids(flows, integrationType)
  const usageByType = new Map(flowTypeUsage(flows, integrationType).map(item => [item.flowTypeId, item]))
  return (
    <div className='layoutBuilder-flow-cards'>
      {flows.map(flow => {
        const trimmedName = flow.name?.trim()
        const nameMissing = !trimmedName
        const flowOverLimit = overLimit.has(flow.flow_uuid)
        const flowTypeId = effectiveFlowTypeId(flow, integrationType)
        const usage = usageByType.get(flowTypeId)
        const incompatible = integrationType && !findFlowType(integrationType, flowTypeId)
        // In a Visual Builder integration the catalog's name wins over whatever label was saved.
        const catalogName = flowTypeId && findFlowType(integrationType, flowTypeId)?.name
        const flowTypeLabel = visualBuilderFlowType(integrationType) ? catalogName : flow.flow_type_label || catalogName
        const name = trimmedName || flowTypeLabel || 'Unnamed flow'
        const openGroups = companyUUID ? openFlowGroups({ name, plannedFlowUuid: flow.flow_uuid, implementations, companyUUID }) : []
        const implemented = openGroups.length > 0
        const showImplementIn = Boolean(onAddFlow) && implementations.length > 0
        const showBuild = Boolean(companyUUID && aiPrompt)
        const changeNote = implemented ? flowChangeNote(implementations, flow.flow_uuid) : null
        return (
          <article className={`layoutBuilder-flow-card${incompatible ? ' incompatible' : ''}${nameMissing ? ' needs-name' : ''}${flowOverLimit ? ' over-limit' : ''}`} aria-label={name} key={flow.flow_uuid}>
            <div className='layoutBuilder-flow-card-heading'>
              <dl className='layoutBuilder-flow-identity'>
                <div><dt>Flow name</dt><dd><strong>{name}</strong></dd></div>
                <div><dt>Flow type</dt><dd>{flowTypeLabel || 'Not specified'}</dd></div>
              </dl>
              <div className='layoutBuilder-flow-card-actions'>
                {implemented && (
                  <span className='layoutBuilder-flow-card-action-group layoutBuilder-flow-card-status'>
                    <Tag severity='success' value='Implemented' />
                    {changeNote && <span className='layoutBuilder-changed-since-implemented'>{changeNote}</span>}
                  </span>
                )}
                {(implemented || showImplementIn || aiPrompt) && (
                  <span className='layoutBuilder-flow-card-action-group'>
                    {implemented && <OpenFlowMenu name={name} groups={openGroups} />}
                    {showImplementIn && (
                      <ImplementInIntegration name={name} flow={flow} implementations={implementations} onAddFlow={onAddFlow} loading={preparingFlowUuid === flow.flow_uuid} disabled={addFlowDisabled} />
                    )}
                    {aiPrompt && <CopyButton text={aiPrompt(flow)} label='Copy AI prompt' title='Copy an AI prompt for this flow' size='sm' />}
                    {showBuild && implementations.map(implementation => implementations.length === 1
                      ? <NewTabLink key={implementation.partner_uuid} label='Build in Visual Builder' aria-label={`Build ${name} in Visual Builder`} href={newAiFlowPath(companyUUID, implementation.partner_uuid)} />
                      : <NewTabLink key={implementation.partner_uuid} label={`Build in ${implementation.partner_name}`} aria-label={`Build ${name} in Visual Builder for ${implementation.partner_name}`} href={newAiFlowPath(companyUUID, implementation.partner_uuid)} />)}
                  </span>
                )}
                <Button label={readOnly ? 'View' : 'Edit'} aria-label={`${readOnly ? 'View' : 'Edit'} ${name}`} severity='secondary' outlined onClick={event => onOpen(flow, event.currentTarget)} />
              </div>
            </div>
            <p className='layoutBuilder-flow-notes'>
              <strong>Notes</strong>
              <span className='layoutBuilder-flow-notes-text'>{flow.notes?.trim() ? flow.notes : NO_NOTES}</span>
            </p>
            {incompatible && <p className='layoutBuilder-flow-incompatible'>Not allowed for this integration type</p>}
            {nameMissing && <p className='layoutBuilder-flow-name-missing'>Flow name is required</p>}
            {flowOverLimit && (
              <p className='layoutBuilder-flow-over-limit'>
                <i className='pi pi-exclamation-triangle' aria-hidden='true' />
                {flowOverLimitWarning({ count: usage.count, name: usage.name, max: usage.max })}
              </p>
            )}
            <div className='layoutBuilder-flow-endpoints'>
              <Endpoint label='Source' side={flow.source} />
              <Endpoint label='Destination' side={flow.destination} />
            </div>
            {onRemove && (
              <div className='layoutBuilder-flow-card-footer'>
                <Button label='Remove flow from layout' aria-label={`Remove ${name} from layout`} severity='danger' outlined onClick={() => onRemove(flow)} />
              </div>
            )}
          </article>
        )
      })}
    </div>
  )
}
